<script>
import { v4 as uuidv4 } from 'uuid';

import { publish } from 'creditu-events-lib';
import { PluggyConnect } from 'pluggy-connect-sdk';
import { ProspectDocumentsPaperlessUpload } from '@/domain/events/paperless-evaluation';
import { verifyToken } from '@/application/services/api-clients/prospects';
import { getDocumentList } from '@/application/services/api-clients/document-list';
import { ProspectEvaluationRequest } from '@/domain/prospect-evaluation-request';
import { getProspectById, addCodebtorDocument, addProspectDocument, sendNotification, getProspectDocument } from '@/application/services/api-clients/prospects-leads';
import { initializeChat } from '@/application/services/third-party/chat';
import { ProspectsClient } from '@/application/services/api-clients/prospects-client';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';
import { widgetIsUse, getExternalProviderConnectToken } from '@/application/services/api-clients/prospects-evaluation';

import CrediExperto from '@/ui/components/internal/CrediExperto.vue';
import Timeline from '@/ui/components/internal/leads/Timeline.vue';
import constants from '@/domain/constants/documents';

import Header from '@/ui/components/Header.vue';
import { Headers } from '@/application/services/api-clients/headers';
import { IdMaker } from '@/application/services/utils/id-maker';
import { simulatorBackend } from '@/application/api';
import ProspectInformation from '@/ui/components/public/document/ProspectInfo.vue';
import HighlightSimulation from '@/ui/components/internal/HighlightSimulation.vue';
import { getSafe } from '@/application/services/get-safe';
import documentFormI18n from './document-form.i18n.json';
import Spinner from '../../components/Spinner.vue';

export default {
  name: 'DocumentForm',
  components: {
    HighlightSimulation,
    ProspectInformation,
    Header,
    CrediExperto,
    Timeline,
    Spinner
  },
  i18n: documentFormI18n,
  props: {
    prospectInformationProps: { type: Object, required: false }
  },
  data() {
    const country = this.$i18n.locale;
    return {
      disableButtonExternalProvider: false,
      verifierItemId: false,
      timekey: true,
      activeTab: 0,
      userIsBroker: false,
      currentUser: this.$store.state.currentUser || 'PROSPECT',
      country,
      prospectInformation: null,
      isLoading: false,
      isLoadingDocument: false,
      documentsUploaded: false,
      uploadedByRole: null,
      uploadedById: null,
      documentsList: null,
      codebtorDocumentsList: null,
      hasCodebtor: false,
      response: true,
      employmentType: null,
      checkEnablePreApproval: false,
      enableApprovalButton: true,
      approvalType: { status: '', subStatus: '' },
      buttonType: { buttonName: 'buttonNamePreApproval' }
    };
  },
  computed: {
    someFileIsNotEmpty() {
      try {
        return this.documentsList.some(({ documents }) => documents.some(doc => doc.uploaded));
      } catch {
        return false;
      }
    },
    filesToSave() {
      try {
        return this.documentsList.some(({ documents }) => documents.some(doc => doc.file !== null))
           || this.codebtorDocumentsList.some(({ documents }) => documents.some(doc => doc.file !== null));
      } catch {
        return false;
      }
    }
  },
  async created() {
    try {
      const loadAutomaticHoleriteToggle = await getFeatureToggleByName('load-holerite-paperless');
      this.loadAutomaticHoleriteToggle = loadAutomaticHoleriteToggle.isActive;
    } catch (error) {
      throw error.message;
    }
  },
  async mounted() {
    if (window.location.href && !window.location.href.includes('leads')) {
      initializeChat();
    }
    this.userIsBroker = this.$route.query.broker === null;
    if (this.currentUser !== 'PROSPECT') {
      this.uploadedByRole = this.userIsBroker ? 'BROKER' : 'EXECUTIVE';
      this.uploadedById = this.currentUser.user._id;
      this.prospectInformation = { ...this.prospectInformationProps };
      this.approvalType = { status: this.prospectInformation.status, subStatus: this.prospectInformation.subStatus };
      await this.accessProspectInformation('EXECUTIVE');
      this.updateButtonAction();
    } else {
      this.uploadedByRole = 'PROSPECT';
      await this.accessProspectInformation('PROSPECT');
      this.updateButtonAction();
    }
  },
  methods: {
    async publishProspectDocumentsPaperlessUpload() {
      publish(new ProspectDocumentsPaperlessUpload(
        new Date(),
        {
          idNumber: this.prospectInformation.idNumber,
          provider: 'PLUGGY'
        },
        {
          country: this.$i18n.locale,
          origin: 'INTERNAL-FRONTEND'
        }
      ));
    },
    async widgetPluggy(sectionIndex, documentIndex, typeDocumentList, typeConexion) {
      try {
        const connectToken = await getExternalProviderConnectToken();
        const pluggyConnect = new PluggyConnect({
          // eslint-disable-next-line max-len
          connectToken,
          includeSandbox: false,
          onSuccess: itemData => {
            setTimeout(async () => {
              this.verifierItemId = await widgetIsUse(itemData.item.id);
              if (this.verifierItemId && !this.documentsList[sectionIndex].documents[documentIndex].uploaded) {
                this.documentsList[sectionIndex].documents[documentIndex].content = itemData.item.id;
                this.documentsList[sectionIndex].documents[documentIndex].file = typeConexion;
                this.disableButtonExternalProvider = true;
                this.publishProspectDocumentsPaperlessUpload();
              }
            }, 5000);
          },
          onError: error => {
            // eslint-disable-next-line no-console
            console.log(error);
          }
        });
        pluggyConnect.init();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    initializeDocumentList(documentsListIn, isCodebtor, accessRoleType) {
      return documentsListIn.reduce((arrSectionIn, sectionIn, sectionIdx) => {
        const section = { ...sectionIn };
        if (accessRoleType === 'EXECUTIVE') { section.hasSwitch = true; }
        if (accessRoleType === 'PROSPECT') {
          section.documents = section.documents.filter(doc => doc.isMinimal === true);
          section.hasSwitch = isCodebtor;
        }
        section.hasSwitch &&= sectionIdx !== 0;
        section.isSwitched = !section.hasSwitch;
        if (section.documents.length === 0) {
          return arrSectionIn;
        }
        if (!isCodebtor && section.employmentType !== 'all'
        && accessRoleType === 'PROSPECT' && section.employmentType !== this.employmentType) {
          return arrSectionIn;
        }
        return [...arrSectionIn, section];
      }, []);
    },
    isSmallScreen() {
      return window.innerWidth < 1248;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    counterDocuments(arr, i) {
      try {
        return (arr[i].documents).length;
      } catch (_error) {
        return 0;
      }
    },
    counterUploadedDocuments(arr, i) {
      try {
        return (arr[i].documents.filter(document => document.uploaded)).length;
      } catch (_error) {
        return 0;
      }
    },
    counterSavedDocuments(arr, i) {
      return (arr[i].documents.filter(document => document.file !== null)).length;
    },
    filesCompletedDetail(docsListIn) {
      if (!docsListIn) return {};
      return docsListIn.reduce(
        (arrIn, { employmentType, documents }) => {
          const arr = { ...arrIn };
          arr[employmentType] = arr[employmentType] !== false && documents.every(doc => (doc.isMinimal ? doc.uploaded : true));
          return arr;
        },
        {}
      );
    },
    filesCompleted() {
      return {
        // datos de prospecto y codebtor subidos
        prospect: this.filesCompletedDetail(this.documentsList),
        codebtor: this.filesCompletedDetail(this.codebtorDocumentsList)
      };
    },
    filesCompletedByAll() {
      // lista de documentos del prospecto y/o codebtor
      if (this.hasCodebtor) { return this.filesCompleted().prospect.all && this.filesCompleted().codebtor.all; }
      return this.filesCompleted().prospect.all;
    },
    filesCompletedByEmploymentType() {
      const listCompleted = this.filesCompleted();
      return listCompleted.prospect.all && listCompleted.prospect[this.employmentType]
       && (
         this.hasCodebtor
           ? listCompleted.codebtor.all && (listCompleted.codebtor.EMPLOYEE !== true || listCompleted.codebtor.INFORMAL !== true
           ) : true
       );
    },
    async updateButtonAction() {
      if (this.filesCompletedByEmploymentType()) {
        this.enableApproval = true;
        this.checkEnablePreApproval = true;
        this.buttonType = { buttonName: 'buttonNameApproval' };
      } else {
        this.checkEnablePreApproval = false;
        this.buttonType = { buttonName: 'buttonNamePreApproval' };
      }
      return null;
    },
    async getApprovalType() {
      if (this.filesCompletedByEmploymentType()) {
        this.approvalType = { buttonName: 'buttonNameApproval', status: 'APPROVAL', subStatus: 'DOCUMENTS_COMPLETED' };
      } else {
        this.approvalType = { buttonName: 'buttonNamePreApproval', status: 'APPROVAL', subStatus: 'DOCUMENTS_PENDING' };
      }
      return null;
    },
    getClassColorText(dataUploaded, dataRequired) {
      if (dataUploaded === 0) return 'text-init';
      if (dataUploaded >= 1 && dataUploaded < dataRequired) return 'text-progress';
      if (dataUploaded === dataRequired) return 'text-success';
      return 'text-error';
    },
    getClassDocument(e, document) {
      if (document.style) {
        return document.style;
      }
      if (document.label || document.file) {
        return 'has-text-grey';
      }
      return '';
    },
    getClassIconDocument(e, document) {
      if (document.file) {
        return 'has-text-grey';
      }
      if (document.uploaded === true) {
        return 'has-text-primary';
      }
      return document.styleIcon;
    },
    getClassRightColumnDocument(e, document) {
      if (document.styleRightColumn) {
        return document.styleRightColumn;
      }
      return '';
    },
    getClassFullColumnDocument(e, document) {
      if (document.styleFullColumn) {
        return document.styleFullColumn;
      }
      return '';
    },
    async accessProspectInformation(accessRoleType) {
      if (accessRoleType === 'PROSPECT') {
        this.isLoading = true;
        if (this.$route.query.token) {
          try {
            ({ data: this.prospectInformation } = await verifyToken(this.$route.query.token));
            this.isLoading = false;
            this.uploadedById = this.prospectInformation._id;
            this.employmentType = this.prospectInformation.employment;
            this.hasCodebtor = this.prospectInformation.hasCodebtor;
          } catch (_error) {
            this.isLoading = false;
            this.response = 'Error verificando el token';
            this.$router.push({
              name: 're-token',
              params: { data: { token: this.$route.query.token } }
            });
          }
        } else {
          this.isLoading = false;
          this.response = false;
          this.$router.push({
            name: 're-token',
            params: { data: { token: this.$route.query.token } }
          });
        }
      } else {
        ({ employment: this.employmentType, hasCodebtor: this.hasCodebtor } = await getProspectById(this.prospectInformation._id));
      }

      const documentsListBase = await getDocumentList();
      this.documentsList = this.initializeDocumentList(documentsListBase.documentList, false, accessRoleType);
      if (this.hasCodebtor) {
        this.codebtorDocumentsList = this.initializeDocumentList(documentsListBase.codebtorDocumentList, true, accessRoleType);
      }

      if (getSafe(this, 'prospectInformation.documentsList', null)) {
        this.documentsList.forEach((docs, sectionIndex) => {
          if (!docs.documents) return;
          docs.documents.forEach((doc, documentIndex) => {
            // La comparación "upl.documentType === doc.description" es para no romper con la versión anterior que
            // guardaba la traducción del nombre del archivo y no el tipo realmente
            const isUploaded = this.prospectInformation.documentsList.find(upl => upl.documentType === doc.document || upl.documentType === doc.description) != null;
            this.documentsList[sectionIndex].documents[documentIndex].uploaded = isUploaded;
            this.documentsList[sectionIndex].isSwitched ||= isUploaded;
            const revertedDocumentsList = this.prospectInformation.documentsList.map(item => item).reverse();
            const { requestedBy, documentId } = revertedDocumentsList.find(upl => upl.documentType === doc.document || upl.documentType === doc.description) || { requestedBy: 'without requested by' };
            this.documentsList[sectionIndex].documents[documentIndex].isUploadedBy = requestedBy;
            this.documentsList[sectionIndex].documents[documentIndex].documentId = documentId;
            if (this.documentsList[sectionIndex].documents[documentIndex].uploaded && this.documentsList[sectionIndex].documents[documentIndex].paperlessButton) {
              this.disableButtonExternalProvider = true;
            }
          });
        });
      }
      // hasCodebtor
      if (this.hasCodebtor && this.prospectInformation.codebtorDocumentsList) {
        this.codebtorDocumentsList.forEach((docs, sectionIndex) => {
          docs.documents.forEach((doc, documentIndex) => {
            // La comparación "upl.documentType === doc.description" es para no romper con la versión anterior que
            // guardaba la traducción del nombre del archivo y no el tipo realmente
            const isUploaded = this.prospectInformation.codebtorDocumentsList.find(upl => upl.documentType === doc.document || upl.documentType === doc.description) != null;
            this.codebtorDocumentsList[sectionIndex].documents[documentIndex].uploaded = isUploaded;
            this.codebtorDocumentsList[sectionIndex].isSwitched ||= isUploaded;
            const revertedDocumentsList = this.prospectInformation.codebtorDocumentsList.map(item => item).reverse();
            const { requestedBy, documentId } = revertedDocumentsList.find(upl => upl.documentType === doc.document || upl.documentType === doc.description) || { requestedBy: 'without requested by' };
            this.codebtorDocumentsList[sectionIndex].documents[documentIndex].isUploadedBy = requestedBy;
            this.codebtorDocumentsList[sectionIndex].documents[documentIndex].documentId = documentId;
          });
        });
      }
      return this.response;
    },
    async fileReader(file, sectionIndex, documentIndex, typeDocumentList) {
      const reader = await new FileReader();
      reader.onload = e => {
        const [, onlyContent] = e.target.result.split(',');
        (this.getDocumentListByType(typeDocumentList))[sectionIndex].documents[documentIndex].content = onlyContent;
      };
      await reader.readAsDataURL(file);
    },
    getDocumentListByType(type) {
      if (type === 'codebtor') {
        return this.codebtorDocumentsList;
      }
      return this.documentsList;
    },
    async getProspectDocument(documentId) {
      this.isLoadingDocument = true;
      try {
        const documentSignedUrl = await getProspectDocument(documentId);
        window.open(documentSignedUrl, '_blank');
      } catch (_error) {
        this.$buefy.toast.open({
          position: 'is-top-right',
          message: this.$t('error-get-documents'),
          type: 'is-danger',
          duration: 5000
        });
      }
      this.isLoadingDocument = false;
    },
    async saveFiles() {
      this.isLoading = true;
      await this.documentsList.reduce((requests, { documents }, sectionIndex) => {
        return documents.reduce((rqts, {
          file,
          content,
          document,
          person
        }, documentIndex) => {
          if (!file) {
            return rqts;
          }
          if (!this.documentsList[sectionIndex]?.documents[documentIndex]?.uploaded) {
            if (this.documentsList[sectionIndex].documents[documentIndex].content.length > constants.MAX_DOCUMENT_SIZE) {
              this.documentsList[sectionIndex].documents[documentIndex].messageUpload = this.$t('file-too-big');
              return false;
            }
            addProspectDocument(
              this.prospectInformation._id,
              {
                country: this.country || 'BR',
                ownerId: this.prospectInformation.idNumber,
                requestedBy: { type: this.uploadedByRole, id: this.uploadedById },
                fileName: file.name,
                file: content,
                metadata: {
                  person,
                  extension: file.name.split('.')
                    .slice(-1)[0],
                  documentType: this.$t(document)
                }
              }
            ).then(resp => {
              this.documentsList[sectionIndex].documents[documentIndex].content = null;
              this.documentsList[sectionIndex].documents[documentIndex].file = null;
              this.documentsList[sectionIndex].documents[documentIndex].uploaded = true;
              this.documentsList[sectionIndex].documents[documentIndex].messageUpload = null;
              this.documentsList[sectionIndex].documents[documentIndex].isUploadedBy = this.uploadedByRole;
              this.documentsList[sectionIndex].documents[documentIndex].documentId = resp.documentId;
              this.updateButtonAction();
              this.isLoading = false;
            }, () => {
              this.documentsList[sectionIndex].documents[documentIndex].messageUpload = this.$t('server-problems');
              this.isLoading = false;
            });
          }
          return rqts;
        }, requests);
      }, []);
      if (this.hasCodebtor) {
        this.isLoading = true;
        await this.codebtorDocumentsList.reduce((requests, { documents }, sectionIndex) => {
          return documents.reduce((rqts, {
            file,
            content,
            document,
            person
          }, documentIndex) => {
            if (!file) {
              return rqts;
            }
            if (!this.codebtorDocumentsList[sectionIndex]?.documents[documentIndex]?.uploaded) {
              if (this.codebtorDocumentsList[sectionIndex].documents[documentIndex].content.length > constants.MAX_DOCUMENT_SIZE) {
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].messageUpload = this.$t('file-too-big');
                return false;
              }
              addCodebtorDocument(
                this.prospectInformation._id,
                {
                  country: this.country || 'BR',
                  ownerId: this.prospectInformation.idNumber,
                  requestedBy: { type: 'CODEBTOR', id: this.uploadedById },
                  fileName: `${this.$t('codebtor-document')} ${file.name}`,
                  file: content,
                  metadata: {
                    person,
                    extension: file.name.split('.')
                      .slice(-1)[0],
                    documentType: this.$t(document)
                  }
                }
              ).then(resp => {
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].content = null;
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].file = null;
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].uploaded = true;
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].messageUpload = null;
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].isUploadedBy = this.uploadedByRole;
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].documentId = resp.documentId;

                this.updateButtonAction();
                this.isLoading = false;
              }, () => {
                this.codebtorDocumentsList[sectionIndex].documents[documentIndex].messageUpload = this.$t('server-problems');
                this.isLoading = false;
              });
            }
            return rqts;
          }, requests);
        }, []);
      }
      return true;
    },
    submitFiles() {
      this.updateButtonAction();
      this.getApprovalType();
      // recolecta los datos personales del cliente y los documentos subidos
      // para enviarlos al sendNotification
      const headers = new Headers(this.$route, this.$store, new IdMaker());
      const prospectClient = new ProspectsClient(simulatorBackend, headers);
      publish(new ProspectEvaluationRequest(
        new Date(),
        {
          id: uuidv4().toString(),
          country: this.$i18n.locale,
          prospectId: this.prospectInformation._id,
          name: this.prospectInformation.name,
          lastName: this.prospectInformation.lastName,
          email: this.prospectInformation.email
        },
        {
          country: this.$i18n.locale,
          origin: 'INTERNAL-FRONTEND'
        }
      ));
      const uploadedFiles = this.documentsList.map(({ documents }) => documents)
        .flat()
        .filter(({ uploaded }) => uploaded === true);
      const data = {
        prospectId: this.prospectInformation._id,
        name: this.prospectInformation.name,
        lastName: this.prospectInformation.lastName,
        email: this.prospectInformation.email,
        documentsList: uploadedFiles.map(({ description }) => this.$t(description)),
        country: this.country || 'BR'
      };
      this.documentsUploaded = true;
      sendNotification(data);

      if (this.approvalType.subStatus === 'DOCUMENTS_COMPLETED') {
        this.enableApprovalButton = false;
      }
      //! Interactions documentos completados
      prospectClient.addInteractionsProspect(
        this.prospectInformation._id,
        {
          comment: this.$t('prospect-document-completed'),
          status: 'LEAD',
          subStatus: 'DOCUMENTS_COMPLETED',
          interactionDate: new Date().toISOString(),
          createdBy: this.uploadedById
        }
      ).then(() => {
        this.timekey = !this.timekey;
      });
      return true;
    },
    getDocumentsToShow(employmentTypeDocument) {
      return (employmentTypeDocument === 'all' || employmentTypeDocument === this.employmentType) || true;
    }
  }
};
</script>
<template>
  <div>
    <div>
      <b-loading v-model="isLoadingDocument" :is-full-page="true" :can-cancel="false">
        <Spinner />
      </b-loading>
    </div>
    <div>
      <Header id="top" v-model="documentsUploaded" />
      <div class="internal-content">
        <div class="hero-section">
          <div class="curve-down" />
        </div>
        <div class="general-content">
          <div id="document-content">
            <div class="columns custom-columns is-flex-desktop is-vcentered">
              <div class="column">
                <h2 class="title has-text-centered-mobile is-3 has-text-white">
                  {{ $t('section-title') }}
                </h2>
                <HighlightSimulation v-if="prospectInformation" :prospect-id="prospectInformation._id" />
              </div>
              <div class="column is-one-third is is-full-mobile">
                <div class="card-item prospect mt-5">
                  <prospect-information
                    v-if="prospectInformation"
                    :name="prospectInformation.name"
                    :last-name="prospectInformation.lastName"
                    :email="prospectInformation.email" />
                </div>
              </div>
            </div>
            <timeline
              v-if="prospectInformation"
              :key="timekey"
              :prospect-id="prospectInformation._id"
              :prospect-status="prospectInformation.status" />
            <div id="content">
              <div
                v-if="!documentsUploaded
                  && !(counterUploadedDocuments(documentsList, 0) === counterDocuments(documentsList, 0)
                    && (counterUploadedDocuments(documentsList, 1) === counterDocuments(documentsList, 1)
                      || counterUploadedDocuments(documentsList, 2) === counterDocuments(documentsList, 2)))"
                class="document-notification my-5">
                <b-notification
                  type="is-info is-light"
                  aria-close-label="Close notification">
                  {{ $t('message-preapprover') }}
                </b-notification>
              </div>
              <div
                v-if="documentsUploaded && uploadedByRole === 'PROSPECT'"
                class="document-notification my-5">
                <b-notification
                  type="is-success is-light"
                  :closable="false">
                  <div class="success-notification">
                    <img class="clock-icon" src="@/ui/assets/img/clock.gif" alt="reloj">
                    <h4 class="title-color title is-6 has-text-weight-light has-text-justified">
                      <b>Parabéns!</b>
                      {{ $t('48hs-message1') }} Nas próximas <b>48 horas</b> {{ $t('48hs-message2') }}
                    </h4>
                  </div>
                </b-notification>
              </div>
              <div
                v-if="uploadedByRole === 'EXECUTIVE' && filesCompletedByEmploymentType()"
                class="document-notification my-5">
                <b-notification
                  type="is-success is-light"
                  :closable="false">
                  <div class="success-notification">
                    <div class="title-color title is-6 has-text-weight-light has-text-justified">
                      <p>{{ $t('executive-message-uploaded-documents') }}</p>
                    </div>
                  </div>
                </b-notification>
              </div>
              <div class="columns is-variable is-8">
                <div class="column">
                  <div class="card-item document-view">
                    <section>
                      <!-- inicio de tabs -->
                      <b-tabs v-model="activeTab" :class="{ 'tab-no-codebtor': !hasCodebtor }" :size="isSmallScreen() ? 'is-smoll' : 'is-medium'">
                        <b-tab-item :label="hasCodebtor ? $t('documents-owner') :''">
                          <div
                            v-for="({ description: sectionName, employmentType: employmentTypeDocument,
                                      subName: sectionSubName, hasSwitch, documents},sectionIndex) in documentsList"
                            :key="sectionIndex">
                            <div
                              v-if="getDocumentsToShow(employmentTypeDocument)"
                              class="mb-5 section-document">
                              <div>
                                <b-field v-if="hasSwitch">
                                  <b-switch
                                    id="activate-section"
                                    v-model="documentsList[sectionIndex].isSwitched"
                                    size="is-small"
                                    outlined>
                                    <h3 class="title is-5 has-text-primary">{{ documentsList[sectionIndex].description }}</h3>
                                  </b-switch>
                                </b-field>
                                <h3 v-else class="title is-5 has-text-primary"> {{ sectionName }} </h3>
                                <h3 v-if="sectionSubName !== ''" class="control document-item columns is-mobile column is-text-right is-four-fifths blackColorText">
                                  {{ sectionSubName }}
                                </h3>
                                <div v-if="documentsList[sectionIndex].isSwitched">
                                  <div
                                    v-for="({ file, description: documentName, hasButton, buttonText, buttonLink, isUploadedBy, messageUpload }, documentIndex) in documents"
                                    :key="sectionIndex-documentIndex"
                                    :class="getClassFullColumnDocument(this, documentsList[sectionIndex].documents[documentIndex])"
                                    class="py-3">
                                    <div
                                      v-if="documentsList[sectionIndex].documents[documentIndex].label === true">
                                      <div
                                        class="is-text-right"
                                        :class="getClassDocument(this, documentsList[sectionIndex].documents[documentIndex])">
                                        {{ documentName }}<a
                                          v-if="hasButton"
                                          type="is-primary"
                                          class="mx-2"
                                          :href="buttonLink"
                                          target="_blank">
                                          <i class="fa-solid fa-file" /> {{ buttonText }}
                                        </a>
                                      </div>
                                    </div>
                                    <b-field>
                                      <div class="is-block">
                                        <div class="is-flex is-flex-shrink-0 is-justify-content-space-between is-align-items-center is-flex-wrap-wrap input-container">
                                          <div class="text-container-zx">
                                            <div
                                              class="input-text"
                                              :class="getClassDocument(this, documentsList[sectionIndex].documents[documentIndex])">
                                              {{ documentName }}<a
                                                v-if="hasButton"
                                                type="is-primary"
                                                class="mx-2"
                                                :href="buttonLink"
                                                target="_blank">
                                                <i class="fa-solid fa-file" /> {{ buttonText }}
                                              </a>
                                            </div>
                                          </div>
                                          <div v-if="file" :key="sectionIndex+'-docsIndex'">
                                            <div class="document-notification">
                                              <b-tag icon="fa-solid fa-file">{{ file.name }}</b-tag>
                                            </div>
                                            <h1 class="text-error"> {{ messageUpload }}</h1>
                                          </div>
                                          <div v-if="documentsList[sectionIndex].documents[documentIndex].paperlessButton" class="input-button">
                                            <b-tooltip
                                              v-if="loadAutomaticHoleriteToggle && !disableButtonExternalProvider"
                                              multilined
                                              size="is-small"
                                              :label="$t('b-tooltip-br')"
                                              position="is-bottom"
                                              class="icon-color">
                                              <i class="fas fa-lg fa-info-circle is-success" />
                                            </b-tooltip>
                                            <b-button
                                              v-if="loadAutomaticHoleriteToggle"
                                              data-cy="button-external-provider"
                                              class="px-6 btn-pluggy"
                                              :class="disableButtonExternalProvider ? 'btn-pluggy-connected' : 'is-rounded'"
                                              :disabled="disableButtonExternalProvider"
                                              @click="widgetPluggy(sectionIndex, documentIndex, 'prospect',{name:'Conexão bem sucedida'})">
                                              {{ disableButtonExternalProvider ? 'Conta conectada' : 'Conectar agora' }}
                                            </b-button>
                                          </div>
                                          <b-tag v-if="isUploadedBy === 'EXECUTIVE'" id="label-upload" type="is-primary is-light">Creditú</b-tag>
                                          <b-tag v-if="isUploadedBy === 'BROKER'" id="label-upload" type="is-primary is-light">Parceiro</b-tag>
                                          <div
                                            :class="getClassRightColumnDocument(this, documentsList[sectionIndex].documents[documentIndex])"
                                            class="input-icon">
                                            <b-upload
                                              v-if="documentsList[sectionIndex].documents[documentIndex].label !== true"
                                              v-model="documentsList[sectionIndex].documents[documentIndex].file"
                                              accept=".jpg, .png, .pdf"
                                              :disabled="documentsList[sectionIndex].isSwitched === false || documentsList[sectionIndex].documents[documentIndex].uploaded === true"
                                              :class="documentsList[sectionIndex].isSwitched === false ? 'section-inactive' : ''"
                                              class="document-item"
                                              @input="fileReader($event, sectionIndex, documentIndex, 'prospect')">
                                              <b-icon
                                                :class="getClassIconDocument(this, documentsList[sectionIndex].documents[documentIndex])"
                                                size="is-medium"
                                                :icon="!documentsList[sectionIndex].documents[documentIndex].file
                                                  ? (documentsList[sectionIndex].documents[documentIndex].uploaded ? 'check' : 'arrow-up-from-bracket')
                                                  : 'paperclip'" />
                                            </b-upload>
                                          </div>
                                        </div>
                                        <div>
                                          <template>
                                            <div
                                              v-if="documentsList[sectionIndex].documents[documentIndex].uploaded === true &&
                                                currentUser !== 'PROSPECT'"
                                              data-cy="document-icons"
                                              class="document-icons my-2"
                                              @click="getProspectDocument(documentsList[sectionIndex].documents[documentIndex].documentId)">
                                              <i class="fa-solid fa-file-lines" />
                                              <span :id="`icon-${sectionIndex}-${documentIndex}`"> {{ $t("access-to-document") }}</span>
                                            </div>
                                          </template>
                                        </div>
                                      </div>
                                    </b-field>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </b-tab-item>
                        <!-- Form Codeudor -->
                        <b-tab-item v-if="hasCodebtor" :label="$t('codebtor-document')">
                          <div
                            v-for="({
                              description: sectionName,
                              subName: sectionSubName,
                              hasSwitch,
                              documents
                            }, sectionIndex) in codebtorDocumentsList"
                            :key="sectionIndex"
                            class="mb-5 section-document">
                            <b-field v-if="hasSwitch">
                              <b-switch
                                id="activate-section"
                                v-model="codebtorDocumentsList[sectionIndex].isSwitched"
                                size="is-small"
                                outlined>
                                <h3 class="title is-5 has-text-primary">{{ sectionName }}</h3>
                              </b-switch>
                            </b-field>
                            <h3 v-else class="title is-5 has-text-primary">
                              {{ sectionName }}
                            </h3>
                            <h3
                              v-if="sectionSubName !== ''"
                              class="control document-item columns is-mobile column is-text-right is-four-fifths blackColorText">
                              {{ sectionSubName }}
                            </h3>
                            <div v-if="codebtorDocumentsList[sectionIndex].isSwitched">
                              <div
                                v-for="({ file, description: documentName, hasButton, buttonText, buttonLink, isUploadedBy, messageUpload }, documentIndex) in documents"
                                :key="sectionIndex-documentIndex"
                                :class="getClassFullColumnDocument(this, codebtorDocumentsList[sectionIndex].documents[documentIndex])"
                                class="py-3">
                                <div
                                  v-if="codebtorDocumentsList[sectionIndex].documents[documentIndex].label === true"
                                  class="columns is-mobile">
                                  <div
                                    class="column is-text-right"
                                    :class="getClassDocument(this, codebtorDocumentsList[sectionIndex].documents[documentIndex])">
                                    {{ documentName }}<a
                                      v-if="hasButton"
                                      type="is-primary"
                                      class="mx-2"
                                      :href="buttonLink"
                                      target="_blank">
                                      <i class="fa-solid fa-file" /> {{ buttonText }}
                                    </a>
                                  </div>
                                </div>
                                <b-field>
                                  <div class="is-block">
                                    <div class="is-flex is-flex-shrink-0 is-justify-content-space-between is-align-items-center is-flex-wrap-wrap input-container">
                                      <div class="text-container-zx">
                                        <div
                                          class="input-text"
                                          :class="getClassDocument(this, codebtorDocumentsList[sectionIndex].documents[documentIndex])">
                                          {{ documentName }}<a
                                            v-if="hasButton"
                                            type="is-primary"
                                            class="mx-2"
                                            :href="buttonLink"
                                            target="_blank">
                                            <i class="fa-solid fa-file" /> {{ buttonText }}
                                          </a>
                                        </div>
                                      </div>
                                      <div v-if="file" :key="sectionIndex+'-docsIndex'">
                                        <div class="document-notification my-5">
                                          <b-tag icon="fa-solid fa-file">{{ file.name }}</b-tag>
                                        </div>
                                        <h1 class="text-error"> {{ messageUpload }}</h1>
                                      </div>
                                      <b-tag v-if="isUploadedBy === 'EXECUTIVE'" id="label-upload" type="is-primary is-light">Creditú</b-tag>
                                      <b-tag v-if="isUploadedBy === 'BROKER'" id="label-upload" type="is-primary is-light">Parceiro</b-tag>
                                      <div
                                        :class="getClassRightColumnDocument(this, codebtorDocumentsList[sectionIndex].documents[documentIndex])"
                                        class="input-icon">
                                        <b-upload
                                          v-if="codebtorDocumentsList[sectionIndex].documents[documentIndex].label !== true"
                                          v-model="codebtorDocumentsList[sectionIndex].documents[documentIndex].file"
                                          accept=".jpg, .png, .pdf"
                                          :disabled="codebtorDocumentsList[sectionIndex].isSwitched === false || codebtorDocumentsList[sectionIndex].documents[documentIndex].uploaded === true"
                                          :class="codebtorDocumentsList[sectionIndex].isSwitched === false ? 'section-inactive' : ''"
                                          class="document-item"
                                          @input="fileReader($event, sectionIndex, documentIndex, 'codebtor')">
                                          <b-icon
                                            :class="getClassIconDocument(this, codebtorDocumentsList[sectionIndex].documents[documentIndex])"
                                            size="is-medium"
                                            :icon="!codebtorDocumentsList[sectionIndex].documents[documentIndex].file
                                              ? (codebtorDocumentsList[sectionIndex].documents[documentIndex].uploaded ? 'check' : 'arrow-up-from-bracket')
                                              : 'paperclip'" />
                                        </b-upload>
                                      </div>
                                    </div>
                                    <div>
                                      <template>
                                        <div
                                          v-if="codebtorDocumentsList[sectionIndex].documents[documentIndex].uploaded === true &&
                                            currentUser !== 'PROSPECT'"
                                          class="document-icons my-2"
                                          @click="getProspectDocument(codebtorDocumentsList[sectionIndex].documents[documentIndex].documentId)">
                                          <i class="fa-solid fa-file-lines" />
                                          <span>{{ $t("access-to-document") }}</span>
                                        </div>
                                      </template>
                                    </div>
                                  </div>
                                </b-field>
                              </div>
                            </div>
                          </div>
                        </b-tab-item>
                      </b-tabs>
                    </section>
                  </div>
                </div>
                <div class="column is-one-third">
                  <div class="card-item">
                    <h3 class="title is-5 has-text-primary">
                      {{ $t('uploaded-documents-section-title') }}
                    </h3>
                    <!-- cambiar el color de las letras dependiendo de la cantidad en un array -->
                    <!-- types: pessoais, assalariado, empresario, fiscal -->
                    <!-- prospect -->
                    <div
                      v-for="({description: sectionName}, sectionIndex) in documentsList"
                      :key="sectionIndex">
                      <div class="container-count-documents">
                        <h5 :class="getClassColorText(counterUploadedDocuments(documentsList, sectionIndex), counterDocuments(documentsList, sectionIndex))">
                          {{ sectionName }}:
                        </h5>
                        <b>{{ counterUploadedDocuments(documentsList, sectionIndex) }}/{{ counterDocuments(documentsList, sectionIndex) }}</b>
                      </div>
                    </div>

                    <!-- codebtor -->
                    <div v-if="hasCodebtor">
                      <div
                        v-for="({description: sectionName}, sectionIndex) in codebtorDocumentsList"
                        :key="sectionIndex"
                        class="container-count-documents">
                        <h5 :class="getClassColorText(counterUploadedDocuments(codebtorDocumentsList, sectionIndex), counterDocuments(documentsList, sectionIndex))">
                          {{ sectionName }}:
                        </h5>
                        <b>{{ counterUploadedDocuments(codebtorDocumentsList, sectionIndex) }}/{{ counterDocuments(codebtorDocumentsList, sectionIndex) }}</b>
                      </div>
                    </div>
                    <div>
                      <div v-if="!someFileIsNotEmpty" class="has-text-centered mt-6">
                        <img src="@/ui/assets/img/documents.gif" alt="documents" class="documents-icon">
                        <h4 class="title is-6 has-text-grey">
                          {{ $t('no-document-message') }}
                        </h4>
                      </div>
                      <div class="btn-container">
                        <div class="has-text-centered mt-6">
                          <b-button
                            id="save-btn"
                            rounded
                            type="is-success"
                            class="px-6 save-btn"
                            :loading="isLoading"
                            :disabled="!filesToSave"
                            @click="uploadedByRole==='PROSPECT'?(saveFiles(), scrollToTop()):saveFiles()">
                            Salvar documentos
                          </b-button>
                        </div>
                        <div
                          v-if="filesCompletedByAll() && buttonType.buttonName === 'buttonNamePreApproval' && approvalType.subStatus !== 'DOCUMENTS_COMPLETED'"
                          id="div-checkbox-accept-you"
                          class="has-text-centered mt-6">
                          {{ $t('message-pré-aprovação') }}<br>
                          <b-checkbox
                            v-model="checkEnablePreApproval"
                            class="mt-4 has-text-primary">
                            {{ $t('you-accept') }}
                          </b-checkbox>
                        </div>
                        <div
                          id="div-button-to-approved"
                          class="has-text-centered mt-4">
                          <b-button
                            v-if="filesCompletedByAll() && approvalType.subStatus !== 'DOCUMENTS_COMPLETED'"
                            id="send-docs-btn"
                            rounded
                            :disabled="!checkEnablePreApproval || !enableApprovalButton"
                            type="is-success"
                            class="px-6 doc-btn"
                            @click="submitFiles(), scrollToTop()">
                            {{ $t(buttonType.buttonName) }}
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <credi-experto />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="css" scoped>
  .input-container {
    width: 100%;
  }

  .input-text {
    width: 100%;
  }
  .text-container-zx{
    width: 65% !important;
  }

  .input-button {
    width: 25% !important;
    display:flex;
    align-items: center;
    justify-content: center;
  }
  .icon-color {
    color: #00CCD1;
    margin-right: 0.5em;
  }

  .input-icon {
    width: 10%;
  }

  .btn-pluggy {
    width: 8rem;
    background: #E9007F !important;
    color: white;
    font-size: 12px;
    padding: 1.2rem;
  }
  .btn-pluggy-connected {
    /* background: #00CCD1 !important; */
  justify-content: center;
  background-color: #00f3ee !important;
  height: 20px;
  border-radius: 5px;
  color: #fff;
  font-weight: bold;
  padding: 0 4px;
  width: 70px

  }
.document-icons{
  cursor: pointer;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  transition: .5s;
  color: #E9007F;
  }
  .document-icons:hover,
  .document-icons .fa-regular:hover ,
  .document-icons .fa-solid:hover
  {
    color: #CC0078;
  }
.document-icons .fa-regular , .document-icons .fa-solid {
  color: #E9007F!important;
  font-size: 1.2rem;
}

.internal-content {
  background-image: linear-gradient(0deg, #f2fbfb, white 120vh);
  padding-bottom: 80px;
  overflow: hidden;
}

.hero-section {
  background-position: center center;
  background-size: 290%;
  background-repeat: no-repeat;
  background-color: #f2fbfb;
  background-image: url(../../assets/img/bubbles.svg);
  height: 475px;
  margin-bottom: -475px;
}

.curve-down {
  position: relative;
  background-image: url(../../assets/img/curve-down.svg);
  background-size: 100% 50px;
  top: 425px;
  height: 50px;
}

#document-content {
  padding-top: 80px;
}

.section-document {
  border: 1px solid #ececec;
  border-radius: 40px;
  padding: 0.5rem 2rem 0.5rem 2rem;
  transition: 2s;
}

.blackColorText {
  color: black;
}

.document-item {
  display: block;
}

.has-full-border-bottom {
  border-bottom: 1px solid #e7e7e7;
  width: 100%;
}

.has-full-border-top {
  border-top: 1px solid #e7e7e7;
  padding-top: 1.5rem;
}

.section-help {
  background-color: #f2fbfb;
  border: none;
}

.section-inactive {
  opacity: 0.2;
}

.is-ready-to-upload {
  border: 1px dashed #cccccc;
  border-radius: 5px;
  color: #E9007F !important;
}

.is-multiple-upload {
  border: none !important;
}

.column-for-inline-items {
  max-width: 300px;
  border: 1px dashed #cccccc;
  border-radius: 0.7rem;
  margin: 0.5rem;
  padding: 1.3rem;

}

.column.has-text-left,
.column.has-text-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 60px;
}

.title-color {
  color: #00CCD1;
  font-size: 1.1rem;
}

.success-notification {
  display: flex;
  align-items: center;
}

div.column.has-text-right {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.doc-btn {
  background: #00CCD1 !important;
  border: none;
  width: 90% !important;
}

.save-btn {
  width: 90% !important;
  background: #E9007F !important;
}

.text-init {
  color: #868e96;
}

.text-progress {
  color: #868e96;
}

.text-success {
  color: #00CCD1;
}

.text-error {
  color: #dc3545;
}

.container-count-documents {
  display: flex;
  justify-content: space-between;
}

/* Media para eliminar el reloj en mobile */
@media (max-width: 1155px) {
  .internal-content {
    width: 100%;
  }
}

@media (min-width: 769px) {
  .hero-section {
    width: 100%;
    background-image: url(../../assets/img/bubbles.svg);
    background-repeat: no-repeat;
    background-position: center center;
    background-color: #09cefa;
  }
}

@media (max-width: 500px) {
  .has-full-border-bottom {
    margin: auto;
    width: 100%;
  }
    .input-text {
  width: 100% !important;
  margin: 0 auto;
}
.text-container-zx{
  width: 100% !important;
}

.input-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 !important;
  }

.input-button {
  width: 100%;
  margin: 0 auto;
}

.input-icon {
  width: 100% !important;
}
}

@media (max-width: 480px) {
  .clock-icon {
    display: none;
  }
}

@media (max-width: 360px) {
  .card-item.document-view {
    padding: 0;
    position: relative;
    margin-left: -15px;
    width: 112%;
  }

  div.mb-5.section-document {
    display: flex;
    flex-direction: column;
    align-content: space-around;
    align-items: center;
  }

  .has-full-border-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
<style>
.document-notification .notification .media-content {
  overflow: hidden;
}
.clock-icon{
  width: 50px;
}
.documents-icon{
  width: 40px;
}
@media(max-width: 767px) {
  .custom-columns {
    flex-direction: column-reverse;
    display: flex;
  }
  .tags{
    justify-content: center;
  }
}
.card-item.prospect{
  overflow: visible;
}
.tab-no-codebtor .tabs a{
border-bottom-style: none;
}
</style>
